import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedplum, useSubscription } from '@medplum/react';
import {
  ChargeItem,
  ChargeItemDefinition,
  Invoice,
  ServiceRequest,
  Patient,
  Attachment,
} from '@medplum/fhirtypes';
import { getReferenceString } from '@medplum/core';
import { PaymentSource, PaymentStatus } from '../lims-types';
import { ARKON } from '../../../../consts/systems.consts';
import {
  buildExamChargeItem,
  buildDepositChargeItem,
  buildInvoiceForOrder,
  findInvoiceForOrder,
  findChargeItemsForOrder,
  getPaymentSource,
  getInvoiceStatus,
  getDepositTotal,
  getRemainingBalance,
  isFullyPaid,
  setPaymentSourceExtension,
  addPaymentNote,
  fetchChargeItemDefinition,
  getPricesFromDefinition,
  PricingOption,
} from '../utils/payment-helpers';
import { DEFAULT_CURRENCY } from '../constants/payment.consts';

export interface PaymentTrackerState {
  /** The Invoice resource (null if not yet created) */
  invoice: Invoice | null;
  /** Main ChargeItem(s) for the order */
  mainChargeItems: ChargeItem[];
  /** Deposit ChargeItem(s) */
  depositItems: ChargeItem[];
  /** Current payment status */
  paymentStatus: PaymentStatus;
  /** Payment source */
  paymentSource: PaymentSource | undefined;
  /** Total gross amount */
  totalGross: number;
  /** Total discount amount */
  discount: number;
  /** Total net amount (after discounts) */
  totalNet: number;
  /** Total deposits paid */
  depositAmount: number;
  /** Remaining balance (net minus deposits) */
  remainingBalance: number;
  /** Loading state */
  loading: boolean;
  /** Error message */
  error: string | null;
  /** The ChargeItemDefinition for this order's test (if found) */
  chargeDefinition: ChargeItemDefinition | undefined;
  /** Calculated pricing options based on the definition */
  pricingOptions: PricingOption[];
}

export interface PaymentTrackerActions {
  /** Create invoice + charge item for the order */
  createPayment: (params: {
    splits: {
      source: PaymentSource;
      amount: number;
      discount?: number;
    }[];
    testCode?: string;
    testDisplay?: string;
  }) => Promise<void>;
  /** Update the payment splits */
  updateSplits: (
    splits: {
      source: PaymentSource;
      amount: number;
      discount?: number;
    }[]
  ) => Promise<void>;
  /** Update the payment source */
  updateSource: (source: PaymentSource) => Promise<void>;
  /** Update the invoice status */
  updateStatus: (status: PaymentStatus) => Promise<void>;
  /** Update amounts on the invoice */
  updateAmounts: (grossAmount: number, netAmount: number) => Promise<void>;
  /** Record a deposit */
  recordDeposit: (
    amount: number,
    note?: string,
    attachments?: Attachment[]
  ) => Promise<void>;
  /** Mark as fully paid (balanced) */
  markAsPaid: () => Promise<void>;
  /** Add a note to the invoice */
  addNote: (text: string) => Promise<void>;
  /** Refresh data from server */
  refresh: () => Promise<void>;
}

export const usePaymentTracker = (
  serviceRequest: ServiceRequest | undefined,
  patient: Patient | undefined
): PaymentTrackerState & PaymentTrackerActions => {
  const medplum = useMedplum();
  const { t } = useTranslation('lims');

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [mainChargeItems, setMainChargeItems] = useState<ChargeItem[]>([]);
  const [depositItems, setDepositItems] = useState<ChargeItem[]>([]);
  const [chargeDefinition, setChargeDefinition] = useState<
    ChargeItemDefinition | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to Invoice changes
  useSubscription(invoice?.id ? `Invoice?_id=${invoice.id}` : '', (bundle) => {
    const updatedInvoice = bundle.entry?.[0]?.resource as Invoice | undefined;
    if (updatedInvoice) {
      setInvoice(updatedInvoice);
    }
  });

  // Load existing payment data
  const loadPaymentData = useCallback(async () => {
    if (!serviceRequest?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const existingInvoicePromise = findInvoiceForOrder(
        medplum,
        serviceRequest.id,
        patient?.id
      );
      const chargeItemResultPromise = findChargeItemsForOrder(
        medplum,
        serviceRequest.id
      );
      const definitionPromise = fetchChargeItemDefinition(
        medplum,
        serviceRequest
      );

      const existingInvoice = await existingInvoicePromise;
      const chargeItemResult = await chargeItemResultPromise;
      const definition = await definitionPromise;

      setInvoice(existingInvoice || null);
      setMainChargeItems(chargeItemResult.mainItems);
      setDepositItems(chargeItemResult.depositItems);
      setChargeDefinition(definition);
    } catch (err) {
      console.error('loadPaymentData: Error:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load payment data'
      );
    } finally {
      setLoading(false);
    }
  }, [medplum, serviceRequest, patient]);

  useEffect(() => {
    loadPaymentData();
  }, [loadPaymentData]);

  // Computed values
  const paymentStatus = useMemo(
    () => (invoice ? getInvoiceStatus(invoice) : 'draft'),
    [invoice]
  );

  const paymentSource = useMemo(
    () => (invoice ? getPaymentSource(invoice) : undefined),
    [invoice]
  );

  const totalGross = useMemo(() => invoice?.totalGross?.value || 0, [invoice]);
  const totalNet = useMemo(() => invoice?.totalNet?.value || 0, [invoice]);
  const discount = useMemo(
    () => Math.max(0, totalGross - totalNet),
    [totalGross, totalNet]
  );
  const depositAmount = useMemo(
    () => getDepositTotal(depositItems),
    [depositItems]
  );

  const remainingBalance = useMemo(
    () => getRemainingBalance(totalNet, depositItems),
    [totalNet, depositItems]
  );

  const pricingOptions = useMemo(() => {
    const options = chargeDefinition
      ? getPricesFromDefinition(
          chargeDefinition,
          t as (key: string, options?: Record<string, unknown>) => string
        )
      : [];
    return options;
  }, [chargeDefinition, t]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const createPayment = useCallback(
    async (params: {
      splits: { source: PaymentSource; amount: number; discount?: number }[];
      testCode?: string;
      testDisplay?: string;
    }) => {
      if (!serviceRequest || !patient) {
        return;
      }

      // Declared outside try so the catch block can access them for rollback
      const createdChargeItems: ChargeItem[] = [];
      const createdDeposits: ChargeItem[] = [];

      try {
        setLoading(true);
        setError(null);

        let totalGross = 0;
        let totalNet = 0;

        // 1. Create ChargeItems for each split
        for (const split of params.splits) {
          if (split.amount < 0) {
            continue;
          }

          const chargeItem = buildExamChargeItem(
            serviceRequest,
            patient,
            split.amount,
            split.source,
            params.testCode,
            params.testDisplay,
            split.discount
          );
          const createdCI = await medplum.createResource(chargeItem);
          createdChargeItems.push(createdCI);

          totalGross += split.amount;
          totalNet += Math.max(0, split.amount - (split.discount || 0));
        }

        if (createdChargeItems.length === 0) {
          throw new Error('No valid payment splits provided');
        }

        // 2. Create Invoice wrapping all charge items
        const fullyPaid = isFullyPaid(totalNet, createdDeposits);
        const initialStatus: PaymentStatus = fullyPaid
          ? 'balanced'
          : 'issued';

        const inv = buildInvoiceForOrder(
          serviceRequest,
          patient,
          createdChargeItems,
          totalGross,
          totalNet,
          params.testCode,
          params.testDisplay
        );
        inv.status = initialStatus as 'balanced' | 'issued';

        // Add deposit line items if any
        if (createdDeposits.length > 0) {
          inv.lineItem = [
            ...(inv.lineItem || []),
            ...createdDeposits.map((d) => ({
              chargeItemReference: { reference: getReferenceString(d) },
            })),
          ];
        }

        // Always do a fresh lookup to find the draft invoice created at order time,
        // in case the hook's invoice state hasn't loaded yet.
        const existingInvoice =
          invoice ??
          (await findInvoiceForOrder(medplum, serviceRequest.id, patient.id));

        let createdInv;
        if (!existingInvoice?.id) {
          createdInv = await medplum.createResource(inv);
        } else {
          inv.id = existingInvoice.id;
          if (existingInvoice.meta) {
            inv.meta = existingInvoice.meta;
          }
          if (existingInvoice.note) {
            inv.note = existingInvoice.note;
          }
          createdInv = await medplum.updateResource(inv);
        }

        setMainChargeItems(createdChargeItems);
        setInvoice(createdInv);
        setDepositItems(createdDeposits);
      } catch (err) {
        for (const ci of [...createdChargeItems, ...createdDeposits]) {
          if (ci.id) {
            try {
              await medplum.deleteResource(ci.resourceType, ci.id);
            } catch {
              console.error('Rollback: failed to delete ChargeItem', ci.id);
            }
          }
        }
        setError(
          err instanceof Error ? err.message : 'Failed to create payment'
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [medplum, serviceRequest, patient, invoice]
  );

  const updateSource = useCallback(
    async (source: PaymentSource) => {
      if (!invoice?.id) {
        return;
      }

      try {
        const updated = setPaymentSourceExtension(invoice, source);

        const result = await medplum.updateResource(updated);
        setInvoice(result);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to update payment source'
        );
        throw err;
      }
    },
    [medplum, invoice]
  );

  const updateStatus = useCallback(
    async (status: PaymentStatus) => {
      if (!invoice?.id) {
        return;
      }

      try {
        const updated: Invoice = { ...invoice, status };
        const result = await medplum.updateResource(updated);
        setInvoice(result);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to update status'
        );
        throw err;
      }
    },
    [medplum, invoice]
  );

  const updateAmounts = useCallback(
    async (grossAmount: number, netAmount: number) => {
      if (!invoice?.id) {
        return;
      }

      try {
        const fullyPaid = isFullyPaid(netAmount, depositItems);

        const isHospital = getPaymentSource(invoice) === PaymentSource.HOSPITAL;
        const newStatus: PaymentStatus = isHospital
          ? 'balanced'
          : fullyPaid
            ? 'balanced'
            : 'issued';

        const updated: Invoice = {
          ...invoice,
          status: newStatus,
          totalGross: { value: grossAmount, currency: DEFAULT_CURRENCY },
          totalNet: { value: netAmount, currency: DEFAULT_CURRENCY },
        };
        const result = await medplum.updateResource(updated);
        setInvoice(result);

        if (mainChargeItems.length === 1 && mainChargeItems[0].id) {
          const ci = {
            ...mainChargeItems[0],
            priceOverride: { value: grossAmount, currency: DEFAULT_CURRENCY },
          };
          const updatedCI = await medplum.updateResource(ci);
          setMainChargeItems([updatedCI]);
        } else if (mainChargeItems.length > 1) {
          throw new Error(
            'Cannot update amounts directly on a multi-split payment. Please use updateSplits instead.'
          );
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to update amounts'
        );
        throw err;
      }
    },
    [medplum, invoice, mainChargeItems, depositItems]
  );

  const updateSplits = useCallback(
    async (
      splits: {
        source: PaymentSource;
        amount: number;
        discount?: number;
      }[]
    ) => {
      if (!invoice?.id || !serviceRequest || !patient) {
        return;
      }

      try {
        setLoading(true);
        const currentItems = [...mainChargeItems];
        const newMainItems: ChargeItem[] = []; // newly created main ChargeItems (extra splits)
        const newDeposits: ChargeItem[] = [];
        const deletedChargeItemRefs = new Set<string>();
        let totalGross = 0;
        let totalNet = 0;

        const maxLength = Math.max(splits.length, currentItems.length);

        for (let i = 0; i < maxLength; i++) {
          const split = splits[i];
          const existingItem = currentItems[i];

          if (split) {
            const splitNet = Math.max(0, split.amount - (split.discount || 0));
            totalGross += split.amount;
            totalNet += splitNet;

            if (existingItem) {
              const updatedItem: ChargeItem = {
                ...existingItem,
                priceOverride: {
                  value: Math.max(0, split.amount - (split.discount || 0)),
                  currency: DEFAULT_CURRENCY,
                },
              };
              const exts =
                updatedItem.extension?.filter(
                  (e) =>
                    e.url !== ARKON.SD.PAYMENT_SOURCE &&
                    e.url !== ARKON.SD.DISCOUNT
                ) || [];

              exts.push({
                url: ARKON.SD.PAYMENT_SOURCE,
                valueCode: split.source,
              });
              if (split.discount && split.discount > 0) {
                exts.push({
                  url: ARKON.SD.DISCOUNT,
                  valueDecimal: split.discount,
                });
              }
              updatedItem.extension = exts;

              await medplum.updateResource(updatedItem);
            } else {
              // Create new — pass discount so priceOverride and extension are both set
              const chargeItem = buildExamChargeItem(
                serviceRequest,
                patient,
                split.amount,
                split.source,
                undefined,
                undefined,
                split.discount
              );

              if (currentItems.length > 0 && currentItems[0].code) {
                chargeItem.code = currentItems[0].code;
              }

              const createdCI = await medplum.createResource(chargeItem);
              newMainItems.push(createdCI);
            }
          } else if (existingItem?.id) {
            deletedChargeItemRefs.add(
              `${existingItem.resourceType}/${existingItem.id}`
            );
            await medplum.deleteResource(
              existingItem.resourceType,
              existingItem.id
            );
          }
        }

        const fullyPaid = isFullyPaid(totalNet, [
          ...depositItems,
          ...newDeposits,
        ]);
        const newStatus = fullyPaid ? 'balanced' : 'issued';

        // Prune lineItem entries for deleted ChargeItems, then append newly
        // created main items and deposits so the invoice stays fully linked.
        const prunedLineItems = (invoice.lineItem || []).filter((li) => {
          const ref = li.chargeItemReference?.reference;
          return !ref || !deletedChargeItemRefs.has(ref);
        });

        const updatedInvoice: Invoice = {
          ...invoice,
          totalGross: { value: totalGross, currency: DEFAULT_CURRENCY },
          totalNet: { value: totalNet, currency: DEFAULT_CURRENCY },
          status: newStatus as 'balanced' | 'issued',
          lineItem: [
            ...prunedLineItems,
            ...newMainItems.map((ci) => ({
              chargeItemReference: { reference: getReferenceString(ci) },
            })),
            ...newDeposits.map((d) => ({
              chargeItemReference: { reference: getReferenceString(d) },
            })),
          ],
        };

        const result = await medplum.updateResource(updatedInvoice);
        setInvoice(result);

        await loadPaymentData();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to update splits'
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [
      medplum,
      invoice,
      mainChargeItems,
      depositItems,
      serviceRequest,
      patient,
      loadPaymentData,
    ]
  );

  const recordDeposit = useCallback(
    async (amount: number, note?: string, attachments?: Attachment[]) => {
      if (!patient || mainChargeItems.length === 0) {
        return;
      }

      try {
        setLoading(true);

        // Create deposit ChargeItem
        const depositCI = buildDepositChargeItem(
          mainChargeItems[0],
          patient,
          amount,
          attachments
        );
        const createdDeposit = await medplum.createResource(depositCI);
        setDepositItems((prev) => [...prev, createdDeposit]);

        // Add deposit as line item to invoice and update status
        if (invoice?.id) {
          const updatedDeposits = [...depositItems, createdDeposit];
          const fullyPaid = isFullyPaid(invoice.totalNet?.value || 0, updatedDeposits);

          const updated: Invoice = {
            ...invoice,
            status: fullyPaid ? 'balanced' : 'issued',
            lineItem: [
              ...(invoice.lineItem || []),
              {
                chargeItemReference: {
                  reference: getReferenceString(createdDeposit),
                },
              },
            ],
          };
          const baseNoteText = t('payment.labels.depositNote', {
            amount,
            currency: DEFAULT_CURRENCY,
          });
          const noteText = note?.trim()
            ? `${baseNoteText} — ${note.trim()}`
            : baseNoteText;
          const withNote = addPaymentNote(updated, noteText);
          const result = await medplum.updateResource(withNote);
          setInvoice(result);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to record deposit'
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [medplum, patient, mainChargeItems, invoice, depositItems, t]
  );

  const markAsPaid = useCallback(async () => {
    if (!invoice?.id) {
      return;
    }

    try {
      const updated = addPaymentNote(
        { ...invoice, status: 'balanced' },
        'Payment completed'
      );
      const result = await medplum.updateResource(updated);
      setInvoice(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark as paid');
      throw err;
    }
  }, [medplum, invoice]);

  const addNoteAction = useCallback(
    async (text: string) => {
      if (!invoice?.id) {
        return;
      }

      try {
        const updated = addPaymentNote(invoice, text);
        const result = await medplum.updateResource(updated);
        setInvoice(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add note');
        throw err;
      }
    },
    [medplum, invoice]
  );

  return {
    invoice,
    depositItems,
    paymentStatus,
    paymentSource,
    totalGross,
    discount,
    totalNet,
    depositAmount,
    remainingBalance,
    loading,
    error,
    chargeDefinition,
    pricingOptions,
    mainChargeItems, // Expose for UI
    createPayment,
    updateSource,
    updateStatus,
    updateAmounts,
    updateSplits,
    recordDeposit,
    markAsPaid,
    addNote: addNoteAction,
    refresh: loadPaymentData,
  };
}
